import React, { useEffect, useState } from 'react';
import { useAdminStore } from '@/store/adminStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Package, Edit, Users, MessageCircle, CreditCard, Save, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

export default function AdminPackagesPage() {
  const { t } = useI18n();
  const { packages, fetchPackages, updatePackage } = useAdminStore();
  const [editDialog, setEditDialog] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const openEdit = (pkg) => {
    setForm({
      name: pkg.name,
      monthly_price: pkg.monthly_price,
      yearly_price: pkg.yearly_price || (pkg.monthly_price * 12),
      personnel_limit: pkg.personnel_limit,
      monthly_message_limit: pkg.monthly_message_limit,
      daily_message_limit: pkg.daily_message_limit,
      cari_limit: pkg.cari_limit,
      payment_limit: pkg.payment_limit,
      is_unlimited_personnel: pkg.is_unlimited_personnel || false,
      is_unlimited_messages: pkg.is_unlimited_messages || false,
      features: (pkg.features || []).join('\n'),
    });
    setEditDialog(pkg);
  };

  const handleSave = async () => {
    const data = {
      ...form,
      monthly_price: parseFloat(form.monthly_price) || 0,
      yearly_price: parseFloat(form.yearly_price) || 0,
      personnel_limit: parseInt(form.personnel_limit) || 0,
      monthly_message_limit: parseInt(form.monthly_message_limit) || 0,
      daily_message_limit: parseInt(form.daily_message_limit) || 0,
      cari_limit: parseInt(form.cari_limit) || 0,
      payment_limit: parseInt(form.payment_limit) || 0,
      features: form.features.split('\n').filter(Boolean),
    };
    delete data.yearly_discount_percent;
    const res = await updatePackage(editDialog.id, data);
    if (res.success) {
      toast.success(t('packageUpdated'));
      setEditDialog(null);
      fetchPackages();
    } else {
      toast.error(res.error || 'Güncelleme başarısız');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-packages-page">
      <div>
        <h1 className="font-heading text-xl sm:text-3xl font-bold">Paket Yönetimi</h1>
        <p className="mt-1 text-sm text-muted-foreground">Abonelik paketlerini düzenleyin. Değişiklikler anında yansır.</p>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="relative transition-all hover:shadow-md" data-testid={`package-card-${pkg.slug}`}>
            {pkg.slug === 'profesyonel' && <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2">En Popüler</Badge>}
            <CardHeader className="pb-2 px-4 sm:px-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                {pkg.name}
              </CardTitle>
              <CardDescription className="text-xs">{pkg.slug === 'trial' ? '7 günlük deneme' : 'Yıllık abonelik'}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 px-4 sm:px-6">
              <div>
                {pkg.monthly_price === 0 ? (
                  <p className="font-heading text-2xl sm:text-3xl font-bold text-primary">Ücretsiz</p>
                ) : (
                  <>
                    <p className="font-heading text-2xl sm:text-3xl font-bold text-primary">
                      {(pkg.yearly_price || (pkg.monthly_price * 12)).toLocaleString('tr-TR')} TL
                    </p>
                    <p className="text-xs text-muted-foreground">/ yıl (Aylık {pkg.monthly_price.toLocaleString('tr-TR')} TL)</p>
                  </>
                )}
              </div>

              <Separator />

              <div className="space-y-1.5 text-xs sm:text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-muted-foreground"><Users className="h-3.5 w-3.5" />Çalışan</span>
                  <span className="font-medium">{pkg.is_unlimited_personnel ? 'Sınırsız' : pkg.personnel_limit}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-muted-foreground"><MessageCircle className="h-3.5 w-3.5" />Aylık Mesaj</span>
                  <span className="font-medium">{pkg.is_unlimited_messages ? 'Sınırsız' : pkg.monthly_message_limit}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-muted-foreground"><CreditCard className="h-3.5 w-3.5" />Cari</span>
                  <span className="font-medium">{pkg.cari_limit >= 9999 ? 'Sınırsız' : pkg.cari_limit}</span>
                </div>
              </div>

              <Button variant="outline" className="w-full" size="sm" onClick={() => openEdit(pkg)} data-testid={`edit-package-${pkg.slug}`}>
                <Edit className="mr-1.5 h-3.5 w-3.5" />Düzenle
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-package-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              Düzenle - {editDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Paket Adı</Label>
              <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="text-sm" data-testid="pkg-name-input" />
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Aylık Fiyat (TL)</Label>
                <Input type="number" min={0} value={form.monthly_price || 0} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} className="text-sm" data-testid="pkg-price-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Yıllık Fiyat (TL)</Label>
                <Input type="number" min={0} value={form.yearly_price || 0} onChange={(e) => setForm({ ...form, yearly_price: e.target.value })} className="text-sm" data-testid="pkg-yearly-price-input" />
              </div>
            </div>
            <Separator />
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Personel Limiti</Label>
                <Input type="number" min={0} value={form.personnel_limit || 0} onChange={(e) => setForm({ ...form, personnel_limit: e.target.value })} disabled={form.is_unlimited_personnel} className="text-sm" data-testid="pkg-personnel-input" />
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.is_unlimited_personnel || false} onChange={(e) => setForm({ ...form, is_unlimited_personnel: e.target.checked })} />
                  Sınırsız
                </label>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Aylık Mesaj Limiti</Label>
                <Input type="number" min={0} value={form.monthly_message_limit || 0} onChange={(e) => setForm({ ...form, monthly_message_limit: e.target.value })} disabled={form.is_unlimited_messages} className="text-sm" data-testid="pkg-message-input" />
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={form.is_unlimited_messages || false} onChange={(e) => setForm({ ...form, is_unlimited_messages: e.target.checked })} />
                  Sınırsız
                </label>
              </div>
            </div>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Günlük Mesaj</Label>
                <Input type="number" min={0} value={form.daily_message_limit || 0} onChange={(e) => setForm({ ...form, daily_message_limit: e.target.value })} className="text-sm" data-testid="pkg-daily-msg-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Cari Limiti</Label>
                <Input type="number" min={0} value={form.cari_limit || 0} onChange={(e) => setForm({ ...form, cari_limit: e.target.value })} className="text-sm" data-testid="pkg-cari-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Ödeme Limiti</Label>
                <Input type="number" min={0} value={form.payment_limit || 0} onChange={(e) => setForm({ ...form, payment_limit: e.target.value })} className="text-sm" data-testid="pkg-payment-input" />
              </div>
            </div>
            <Separator />
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Özellikler (her satır bir özellik)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={form.features || ''}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                data-testid="pkg-features-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditDialog(null)} className="w-full sm:w-auto">{t('cancel')}</Button>
            <Button onClick={handleSave} className="w-full sm:w-auto" data-testid="save-package-btn">
              <Save className="mr-1.5 h-4 w-4" />Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
